import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { unsubscribeNewsletter } from "@/lib/newsletter.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Check } from "lucide-react";

export const Route = createFileRoute("/newsletter/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — BidNova Auctions" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({ token: z.string().uuid().optional().parse(s.token) }),
  component: UnsubscribePage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const fn = useServerFn(unsubscribeNewsletter);
  const [state, setState] = useState<"loading" | "done" | "missing">("loading");
  useEffect(() => {
    if (!token) {
      setState("missing");
      return;
    }
    fn({ data: { token } }).finally(() => setState("done"));
  }, [token, fn]);
  return (
    <div className="mx-auto max-w-lg px-6 py-24 text-center">
      <h1 className="font-display text-3xl text-white">Newsletter</h1>
      {state === "loading" && <p className="mt-4 text-muted-foreground">Processing…</p>}
      {state === "missing" && (
        <p className="mt-4 text-muted-foreground">
          This unsubscribe link is incomplete. Please use the link in the email footer.
        </p>
      )}
      {state === "done" && (
        <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-emerald-100">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-500/20">
            <Check className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg">You've been unsubscribed</p>
          <p className="mt-1 text-sm text-emerald-200/80">
            You won't receive marketing emails from BidNova going forward.
          </p>
        </div>
      )}
    </div>
  );
}
